// Когда отправлять очередь: при старте, при появлении сети, при возврате на вкладку, раз в 20 с
// и сразу после входа или разблокировки (если предыдущая попытка упёрлась в 401 или 423). После доставки обновляет
// кэш тренировок и плана, а после успешной — и сводку «Сегодня».

import type { QueryClient } from '@tanstack/react-query'

import { authKeys, type AuthState } from '@/api/auth'
import { invalidateToday } from '@/api/queryClient'
import { captureIdentity } from '@/api/identity'

import { flush, isAuthBlocked, type FlushResult } from './queue'

const RETRY_EVERY_MS = 20_000

let client: QueryClient | null = null

async function flushAndRefresh(): Promise<FlushResult | null> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return null
  const identity = captureIdentity()
  if (!identity.id) return null
  const queryClient = client
  const result = await flush()
  if (!identity.isCurrent() || client !== queryClient) return result
  if (result.sent + result.failed > 0 && client) {
    void client.invalidateQueries({ queryKey: ['training'] })
    void client.invalidateQueries({ queryKey: ['plan'] })
  }
  if (result.sent > 0 && client) void invalidateToday(client)
  return result
}

/** Отправить очередь, как только получится (без ожидания результата). */
export function flushSoon(): void {
  void flushAndRefresh()
}

/** Запустить фоновую отправку очереди; возвращает функцию остановки. */
export function startQueueSync(queryClient: QueryClient): () => void {
  const identity = captureIdentity()
  client = queryClient
  const auto = () => {
    if (identity.isCurrent() && !isAuthBlocked()) flushSoon()
  }
  const onVisible = () => {
    if (document.visibilityState === 'visible') auto()
  }
  window.addEventListener('online', auto)
  document.addEventListener('visibilitychange', onVisible)
  const interval = setInterval(auto, RETRY_EVERY_MS)

  // Вошёл заново после 401 или разблокировал после 423 — досылаем очередь.
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || !isAuthBlocked()) return
    const key = event.query.queryKey
    if (key[0] !== authKeys.state[0] || key[1] !== authKeys.state[1]) return
    const state = event.query.state.data as AuthState | undefined
    if (identity.isCurrent() && state?.authenticated && !state.locked) flushSoon()
  })

  auto()
  return () => {
    window.removeEventListener('online', auto)
    document.removeEventListener('visibilitychange', onVisible)
    clearInterval(interval)
    unsubscribe()
    if (client === queryClient) client = null
  }
}
